import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link } from '@tanstack/react-router';

import {
  ApiError,
  shaktiApi,
  type AgentDescriptor,
  type InvokeAgentResponse,
} from '../api/client';

/**
 * glistening-zebra Phase F1 — Planning Chat route.
 *
 * Surfaces the planning side of agent interaction: pick a planning-
 * capable agent, send a prompt, get an execution_id back, then
 * jump into the live agent-activity stream to watch it run.
 *
 * This is intentionally outbound-focused. The inbound stream
 * (tool calls, decisions, completions) is already covered by the
 * existing `/dashboard/activity` route; duplicating that view
 * here would just split the streaming buffer across two pages.
 *
 * Embedded inside Tauri / VSCode webviews, this is the same view —
 * keeping all chat state in React means F2 and F3 don't have to
 * reimplement the composer.
 */

/** Local turn record kept in component state — not persisted yet.
 * Future iteration: hydrate from `/v1/agents/executions/<id>` so
 * a refresh restores the conversation. */
interface ChatTurn {
  id: string;
  agent_name: string;
  prompt: string;
  execution_id: string | null;
  error: string | null;
  ts: string;
}
export function PlanningChatRoute(): JSX.Element {
  const [agents, setAgents] = useState<AgentDescriptor[]>([]);
  const [agentName, setAgentName] = useState<string>('');
  const [prompt, setPrompt] = useState('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);

  // Load agent list once. Filter is intentionally lenient — the
  // server returns Debug-formatted capability strings (see
  // routes.rs:1841), so anything that name-matches "plan" is a
  // good first pass; the user can always pick another from the
  // full list.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const list = await shaktiApi.listAgents({ signal: controller.signal });
        setAgents(list.agents);
        // Auto-select first agent whose name suggests planning.
        const planAgent =
          list.agents.find((a) => /plan|coord|architect/i.test(a.agent_name)) ??
          list.agents[0];
        if (planAgent) setAgentName(planAgent.agent_name);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setAgentsError(
          err instanceof ApiError ? err.body?.error ?? err.message : 'Failed to load agents',
        );
      } finally {
        setLoadingAgents(false);
      }
    })();
    return () => controller.abort();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = prompt.trim();
    if (!agentName) return;
    if (!trimmed) return;

    setSubmitting(true);
    const optimistic: ChatTurn = {
      id: cryptoRandomId(),
      agent_name: agentName,
      prompt: trimmed,
      execution_id: null,
      error: null,
      ts: new Date().toISOString(),
    };
    setTurns((prev) => [optimistic, ...prev]);
    setPrompt('');

    try {
      const resp: InvokeAgentResponse = await shaktiApi.invokeAgent(
        agentName,
        // The server scans `body.input` for prompt-injection patterns
        // (routes.rs:1904) before dispatching to the LLM. Wrap the
        // free-form prompt so the scan still walks the JSON tree.
        { message: trimmed, source: 'planning-chat' },
      );
      setTurns((prev) =>
        prev.map((t) =>
          t.id === optimistic.id ? { ...t, execution_id: resp.execution_id } : t,
        ),
      );
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.body?.error ?? err.message : 'Invoke failed';
      setTurns((prev) =>
        prev.map((t) => (t.id === optimistic.id ? { ...t, error: msg } : t)),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-labelledby="planning-chat-heading">
      <header className="mb-6">
        <p className="text-2xs uppercase tracking-wider text-fg-muted">Plan</p>
        <h1 id="planning-chat-heading" className="mt-1 text-2xl font-semibold">
          Planning chat
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-fg-secondary">
          Send planning prompts to any agent in the registry. Each invocation produces an
          execution; click the link to watch its tool calls and decisions stream into the
          activity panel.
        </p>
      </header>

      {agentsError && (
        <div
          role="alert"
          className="mb-6 rounded-md border border-warning bg-bg-surface p-4 text-sm text-warning"
        >
          {agentsError}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        className="mb-8 grid gap-3 rounded-md border border-border-subtle bg-bg-surface p-4"
      >
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>Agent</span>
          <select
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            className="rounded-sm border border-border-subtle bg-bg-surface-elevated px-3 py-2 text-xs text-fg-primary disabled:opacity-50"
            disabled={loadingAgents || agents.length === 0}
            data-testid="planning-chat-agent-select"
          >
            {loadingAgents && <option value="">Loading agents…</option>}
            {!loadingAgents && agents.length === 0 && (
              <option value="">No agents registered</option>
            )}
            {agents.map((a) => (
              <option key={a.agent_name} value={a.agent_name}>
                {a.agent_name} · {shortCaps(a.capabilities)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>Prompt</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe what you want the agent to plan…"
            rows={4}
            className="resize-y rounded-sm border border-border-subtle bg-bg-surface-elevated px-3 py-2 font-mono text-xs text-fg-primary"
            data-testid="planning-chat-prompt-input"
          />
        </label>

        <div className="flex items-center justify-between">
          <p className="text-2xs text-fg-muted">
            Inputs are scanned for prompt-injection patterns before dispatch.
          </p>
          <button
            type="submit"
            disabled={submitting || !agentName || !prompt.trim()}
            className="rounded-sm bg-accent px-4 py-2 text-xs font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
            data-testid="planning-chat-submit"
          >
            {submitting ? 'Dispatching…' : 'Send'}
          </button>
        </div>
      </form>

      <section aria-labelledby="turns-heading">
        <h2 id="turns-heading" className="mb-2 text-sm font-semibold">
          Recent turns
        </h2>
        {turns.length === 0 ? (
          <p className="text-xs text-fg-muted">
            No turns yet. Send a prompt above to dispatch an execution.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="planning-chat-turns">
            {turns.map((t) => (
              <li
                key={t.id}
                className="rounded-md border border-border-subtle bg-bg-surface p-4"
              >
                <header className="mb-2 flex items-baseline justify-between gap-3 text-2xs text-fg-muted">
                  <span className="font-mono text-fg-primary">{t.agent_name}</span>
                  <time dateTime={t.ts}>{new Date(t.ts).toLocaleTimeString()}</time>
                </header>
                <pre className="whitespace-pre-wrap break-words font-mono text-xs text-fg-secondary">
                  {t.prompt}
                </pre>
                {t.error ? (
                  <p className="mt-2 text-xs text-warning">{t.error}</p>
                ) : t.execution_id ? (
                  <p className="mt-2 text-xs text-fg-muted">
                    Execution&nbsp;
                    <code className="font-mono text-2xs">{t.execution_id}</code> —&nbsp;
                    <Link
                      to="/dashboard/activity"
                      className="text-accent underline hover:text-accent-hover"
                    >
                      open in activity stream
                    </Link>
                  </p>
                ) : (
                  <p className="mt-2 text-2xs text-fg-muted">Dispatching…</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

/** Take the leading word of the Debug-formatted capability set so
 * the dropdown stays narrow. The full string is available via
 * inspect / the agent registry endpoint if anyone needs it. */
function shortCaps(caps: string): string {
  const trimmed = caps.replace(/^Capabilities?\s*\(\s*/i, '').replace(/\)\s*$/, '');
  return trimmed.length > 40 ? `${trimmed.slice(0, 37)}…` : trimmed;
}

function cryptoRandomId(): string {
  // crypto.randomUUID is available in every modern browser + Node;
  // fallback used in tests where the global is missing.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}
